import React, { useEffect, useState } from 'react'
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form'
import { useTranslate, useNotify, required, FormDataConsumer } from 'react-admin'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { Delete, Add } from '@mui/icons-material'
import Button from '@mui/material/Button'
import { Grid, Skeleton } from '@mui/material'
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'
import { dueDateServices } from 'src/services/reservation/due-date/dueDate.service'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'

const ProductSelectInput = ({
  index,
  optionsProduct,
  loadingProduct,
  handleProductChange,
  editMode,
  business_unit_id,
  field,
}) => {
  const { control } = useFormContext()
   const translate = useTranslate()
  // Usar watch para observar los valores actuales de due_date_method_relation
  const record = useWatch({
    control,
    name: `due_date_method_relation.${index}`,
    defaultValue: null,
  })

  return (
    <EditInputsWrap label={translate('ra.field.product')} required md={6}>
      {loadingProduct ? (
        <Skeleton variant='rectangular' width='100%' height={40} />
      ) : (
        <>
          {record && <SelectInput
            source={`due_date_method_relation.${index}.product_id`}
            options={optionsProduct}
            label=''
            optionsText={['product_relation.long_name']}
            optionValue='product_id'
            multiple
            showSearch
            validate={[required()]}
            onChange={
              editMode && !('new_register' in field)
                ? (e) => handleProductChange(e.target.value, index, record, business_unit_id)
                : null
            }
          />}
        </>
      )}
    </EditInputsWrap>
  )
}

export const FieldArrayDueDateMethodBusinessUnit = ({ editMode = true, business_unit_id, record }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { control } = useFormContext()
  const [openModal, setOpenModal] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(null)
  const [optionsProduct, setOptionsProduct] = useState([])
  const [loadingProduct, setLoadingProduct] = useState(false)
  const [previousProducts, setPreviousProducts] = useState({})
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Crear un estado independiente para cada fila utilizando el _id como clave
  const [dueDateMethodsMap, setDueDateMethodsMap] = useState({})
  const [loadingDueDateMethodMap, setLoadingDueDateMethodMap] = useState({})

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'due_date_method_relation',
    keyName: '_id',
  })

  useEffect(() => {
    getProducts(record)
  }, [record])

  useEffect(() => {
    if (confirmDelete) {
      deleteDueDateMethodByService(currentIndex, business_unit_id)
      setOpenModal(false)
      setCurrentIndex(null)
      setConfirmDelete(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete])

  const deleteDueDateMethodByService = async (index) => {
    try {
      const due_date_method = fields[index]
      if (editMode && due_date_method && !('new_register' in due_date_method)) {
        const data = {
          business_unit_id: business_unit_id,
          due_date_method_id: due_date_method?.due_date_method_id?.id || due_date_method?.due_date_method_id,
        }
        const status = await dueDateServices.deleteDueDateMethodByBusinessUnit(
          data?.due_date_method_id,
          data?.business_unit_id
        )
        if (status >= 200 && status <= 204) {
          remove(index)
          notify(`ra.notification.delete_success_due_date_method`, {
            type: 'success',
          })
          return
        }
      }
      notify(`ra.notification.delete_error_due_date_method`, {
        type: 'error',
      })
    } catch (error) {
      console.log('error', error)
      const messaggeKey = error?.response?.data?.detail
      if (messaggeKey) {
        notify(`${messaggeKey}`, {
          type: 'error',
        })
      } else {
        notify(`ra.notification.delete_error_due_date_method`, {
          type: 'error',
        })
      }
    }
  }

  const createDueDateMethodByBusinessUnitByService = async (data) => {
    try {
      await dueDateServices.createDueDateMethodByBusinessUnit(data)
    } catch (error) {
      console.log('createDueDateMethodByBusinessUnitByService error', error)
    }
  }

  const deleteDueDateMethodByBusinessUnitByService = async (
    due_date_method_id,
    business_unit_id,
    product_id
  ) => {
    try {
      await dueDateServices.deleteDueDateMethodByBusinessUnit(
        due_date_method_id,
        business_unit_id,
        product_id
      )
    } catch (error) {
      console.log('deleteDueDateMethodByBusinessUnitByService error', error)
    }
  }

  const handleProductChange = (newProducts, index, record, business_unit_id) => {
    // Verificar si es la carga inicial y evitar la ejecución
    if (isInitialLoad) {
      setPreviousProducts((prev) => ({
        ...prev,
        [index]: newProducts,
      }))
      setIsInitialLoad(false)
      return
    }

    const prevProductsForIndex = previousProducts[index] || [] // Obtener productos anteriores para el índice actual
    const addedProducts = newProducts.filter((product) => !prevProductsForIndex.includes(product))
    const removedProducts = prevProductsForIndex.filter((product) => !newProducts.includes(product))

    if (addedProducts.length > 0 && record?.due_date_method_id?.id) {
      const data = {
        product_id: addedProducts[0],
        business_unit_id: business_unit_id,
        due_date_method_id: record?.due_date_method_id?.id,
      }
      createDueDateMethodByBusinessUnitByService(data)
    }

    if (removedProducts.length > 0 && record?.due_date_method_id?.id) {
      const data = {
        product_id: removedProducts[0],
        business_unit_id: business_unit_id,
        due_date_method_id: record?.due_date_method_id?.id,
      }
      deleteDueDateMethodByBusinessUnitByService(
        data?.due_date_method_id,
        data?.business_unit_id,
        data?.product_id
      )
    }
    setPreviousProducts((prev) => ({
      ...prev,
      [index]: newProducts,
    }))
  }

  const deleteDueDateMethod = (originalIndex, dueDateMethod, remove) => {
    try {
      if (editMode && !('new_register' in dueDateMethod)) {
        setOpenModal(true)
        setCurrentIndex(originalIndex)
      } else {
        remove(originalIndex)
      }
    } catch (error) {
      console.log('deleteDueDateMethod error', error)
    }
  }

  const getProducts = (record) => {
    try {
      setLoadingProduct(true)
      const products = [
        ...new Set([
          ...(record.product_by_work_unit_inheritance || []),
          ...(record.product_work_unit_by_business_unit_relation || []),
        ]),
      ]

      setOptionsProduct(products)
    } catch (error) {
      setOptionsProduct([])
    } finally {
      setLoadingProduct(false)
    }
  }

  const getDueDateMethod = async (name, id) => {
    if (name?.length < 3) {
      setDueDateMethodsMap((prevState) => ({
        ...prevState,
        [id]: [],
      }))
      return
    }
    try {
      setLoadingDueDateMethodMap((prevState) => ({
        ...prevState,
        [id]: true,
      }))
      const dueDateRange = await dueDateServices.getByAllDueDateMethod({ name__ilike: name })
      setDueDateMethodsMap((prevState) => ({
        ...prevState,
        [id]: dueDateRange,
      }))
    } catch (error) {
      setDueDateMethodsMap((prevState) => ({
        ...prevState,
        [id]: [],
      }))
    } finally {
      setLoadingDueDateMethodMap((prevState) => ({
        ...prevState,
        [id]: false,
      }))
    }
  }

  return (
    <>
      <ConfirmationDialog
        title={translate('ra.message.delete_due_date_method')}
        buttonConfirmText={translate('ra.action.to_confirm')}
        buttonCancel={translate('ra.action.cancel')}
        setOpen={setOpenModal}
        setConfirm={setConfirmDelete}
        open={openModal}
      />
      {React.Children.toArray(
        fields.map((field, index) => (
          <div
            key={field._id}
            style={{
              width: '100%',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 10,
            }}
          >
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.due_date_method')} required md={6} className='small'>
                <FormDataConsumer>
                  {({ formData }) => {
                    const options = (dueDateMethodsMap[field._id] || []).filter((elem) => {
                      if (
                        formData.due_date_method_relation[index].due_date_method_relation?.id === elem.id
                      ) {
                        return true
                      }

                      return !formData.due_date_method_relation.find(
                        (product) => product.due_date_method_relation?.id === elem.id
                      )
                    })

                    return (
                      <AutocompleteInput
                        source={`due_date_method_relation.${index}.due_date_method_id`}
                        options={
                          editMode && !('new_register' in field)
                            ? dueDateMethodsMap[field._id] || []
                            : options
                        }
                        loading={loadingDueDateMethodMap[field._id] || false}
                        optionsText={['name']}
                        onSearch={(name) => getDueDateMethod(name, field._id)}
                        resetOnLoad={true}
                        fullWidth
                        validate={required()}
                        disabled={editMode && !('new_register' in field)}
                        defaultValue={formData.due_date_method_relation[index]?.due_date_method_relation}
                      />
                    )
                  }}
                </FormDataConsumer>
              </EditInputsWrap>
              <ProductSelectInput
                index={index}
                optionsProduct={optionsProduct}
                loadingProduct={loadingProduct}
                handleProductChange={handleProductChange}
                editMode={editMode}
                business_unit_id={business_unit_id}
                field={field}
              />
            </Grid>
            <Grid container spacing={3} justifyContent='flex-end' className='addItemSeparator'>
              <Grid item xs={12} sm={12} className='grid' style={{ marginTop: 10 }}>
                <Button
                  onClick={() => deleteDueDateMethod(index, field, remove)}
                  startIcon={<Delete />}
                  color='error'
                >
                  {translate('ra.action.delete')}
                </Button>
              </Grid>
            </Grid>
          </div>
        ))
      )}
      <Button
        startIcon={<Add />}
        onClick={() => {
          append({ due_date_method_id: null, product_id: null, new_register: true })
        }}
      >
        {translate('ra.action.add_specific', {
          name: translate('ra.field.due_date_method'),
        })}
      </Button>
    </>
  )
}
